/**
 * PatientMedicalReport Page
 * Wizard-based medical report export
 * Step 1: Edit & Select - View consultations with editable data and field visibility toggles
 * Step 2: Preview & Export - Clean report preview with export options
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router";
import { toast } from "react-toastify";
import moment from "moment";
import {
  ArrowLeft,
  ArrowRight,
  FileText,
  Eye,
  Edit3,
  RefreshCw,
  CheckSquare,
  Square,
  ChevronsDown,
  ChevronsUp,
} from "lucide-react";

// Layout Components
import Sidebar from "../../components/Sidebar";
import DashboardDoctorHeader from "../../components/MainHeader/DashboardDoctorHeader";
import LoadingPage from "../../components/LoadingPage";

// Medical Report Components
import {
  AppointmentSelector,
  ReportPreview,
  ExportActions,
  PatientHeader,
  CoverInfoEditor,
} from "../../components/MedicalReport";

// Services
import {
  fetchPatientMedicalReportApi,
  fetchPatientFamilyApi,
  generatePDFFromElement,
  generateJSONExport,
  getDefaultSelectedFields,
  EXPORT_FIELDS,
} from "../../services/medicalReportService";

import { fetchDocsWithSignedUrlsApi } from "../../services/documentService";
import { listDoctorProtocolsoinss, listDoctorProtocolsoins } from "../../services/stockProtocolService";
import { listPatientWeightss, listPatientWeights } from "../../services/apiService";

// Context
import { useUserContext } from "../../context/UserContext";

// Styles
import "../../styles/MedicalReport.scss";

// Deep clone helper
const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

const PatientMedicalReport = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { userData } = useUserContext();
  const reportPreviewRef = useRef(null);

  // Wizard step
  const [wizardStep, setWizardStep] = useState("edit"); // "edit" | "preview"

  // Loading states
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingAppointmentData, setIsLoadingAppointmentData] = useState({});
  const [isExporting, setIsExporting] = useState(false);

  // Patient data
  const [patientData, setPatientData] = useState(null);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [selectedFamilyMember, setSelectedFamilyMember] = useState(null);

  // Appointment data - editable copies and original reference
  const originalAppointmentsRef = useRef([]);
  const [editedAppointments, setEditedAppointments] = useState([]);

  // Selection and visibility states
  const [selectedAppointments, setSelectedAppointments] = useState({});
  const [fieldVisibility, setFieldVisibility] = useState({});
  const [collapsedAppointments, setCollapsedAppointments] = useState({});

  // Additional data
  const [appointmentCareSteps, setAppointmentCareSteps] = useState({});
  const [appointmentCharts, setAppointmentCharts] = useState({});
  const [appointmentDocuments, setAppointmentDocuments] = useState({});

  // Cover page editable info
  const [editedDoctorInfo, setEditedDoctorInfo] = useState(null);
  const [editedPatientInfo, setEditedPatientInfo] = useState(null);
  const originalDoctorInfoRef = useRef(null);
  const originalPatientInfoRef = useRef(null);
  const [coverFieldVisibility, setCoverFieldVisibility] = useState({
    doctor: { name: true, address: true, phone: true, email: true },
    patient: { name: true, dob: true, gender: true, age: true },
    medical: { background: true, lifestyle: true, allergies: true, treatments: true },
  });

  // Doctor ID
  const doctorId = useMemo(() => {
    try {
      const raw = localStorage.getItem("doctolikLoginInfo");
      if (!raw) return null;
      const v = JSON.parse(raw);
      return v?.users?.id ?? v?.user?.id ?? v?.doctor?.id ?? v?.data?.user?.id ?? null;
    } catch {
      return null;
    }
  }, []);

  // Initialize field visibility for an appointment (default fields visible)
  const initializeFieldVisibility = useCallback((appointmentId) => {
    const visibility = {};
    Object.entries(EXPORT_FIELDS).forEach(([catKey, cat]) => {
      visibility[catKey] = {};
      cat.fields.forEach(f => {
        visibility[catKey][f.key] = f.default !== false;
      });
    });
    return visibility;
  }, []);

  // Fetch patient data
  const fetchPatientData = useCallback(async (memberId = null) => {
    try {
      setIsLoading(true);
      const targetId = memberId || patientId;
      const response = await fetchPatientMedicalReportApi(targetId);

      if (response?.data?.res === true) {
        setPatientData(response.data);
        const patient = response.data.data?.[0] || {};
        const patientMedInfo = {
          first_name: patient.first_name || "",
          last_name: patient.last_name || "",
          dob: patient.dob || patient.date_of_birth || "",
          gender: patient.gender || "",
          age: patient.age || "",
          medical_background: patient.medical_background || [],
          lifestyle: patient.lifestyle || patient.mode_de_vie || "",
          allergies: patient.allergies || "",
          current_treatments: patient.current_treatments || patient.traitements_en_cours || "",
        };
        setEditedPatientInfo(deepClone(patientMedInfo));
        originalPatientInfoRef.current = deepClone(patientMedInfo);
        const appointmentsList = response.data.Appointment || [];

        // Store originals for reset
        originalAppointmentsRef.current = appointmentsList.map(a => deepClone(a));

        // Create editable copies
        setEditedAppointments(appointmentsList.map(a => deepClone(a)));

        // Initialize selections and visibility
        const initialSelection = {};
        const initialVisibility = {};
        appointmentsList.forEach(apt => {
          initialSelection[apt.id] = true;
          initialVisibility[apt.id] = initializeFieldVisibility(apt.id);
        });
        setSelectedAppointments(initialSelection);
        setFieldVisibility(initialVisibility);
      } else {
        toast.error(response?.data?.msg || "Échec du chargement des données du patient");
      }
    } catch (error) {
      console.error("Error fetching patient data:", error);
      toast.error("Erreur lors du chargement des données. Veuillez réessayer.");
    } finally {
      setIsLoading(false);
    }
  }, [patientId, initializeFieldVisibility]);

  // Fetch family members
  const fetchFamilyMembers = useCallback(async () => {
    try {
      const response = await fetchPatientFamilyApi(patientId);
      if (response?.data?.res === true) {
        setFamilyMembers(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching family members:", error);
    }
  }, [patientId]);

  // Fetch additional appointment data
  const fetchAppointmentAdditionalData = useCallback(async (appointmentId) => {
    if (!doctorId || !patientId || !appointmentId) return;
    if (isLoadingAppointmentData[appointmentId]) return;

    setIsLoadingAppointmentData(prev => ({ ...prev, [appointmentId]: true }));
    try {
      try {
        const stepsResponse = typeof listDoctorProtocolsoinss === 'function'
          ? await listDoctorProtocolsoinss(doctorId, { patientId, appointmentId })
          : await listDoctorProtocolsoins(doctorId, { patientId, appointmentId });
        setAppointmentCareSteps(prev => ({ ...prev, [appointmentId]: Array.isArray(stepsResponse) ? stepsResponse : [] }));
      } catch { setAppointmentCareSteps(prev => ({ ...prev, [appointmentId]: [] })); }

      try {
        const chartsResponse = typeof listPatientWeightss === 'function'
          ? await listPatientWeightss(doctorId, patientId, appointmentId)
          : await listPatientWeights(doctorId, patientId);
        let items = [];
        const resData = chartsResponse?.data ?? chartsResponse;
        if (Array.isArray(resData)) items = resData;
        else if (resData && typeof resData === 'object') items = resData.weights ?? resData.data ?? resData.items ?? [];
        setAppointmentCharts(prev => ({ ...prev, [appointmentId]: items }));
      } catch { setAppointmentCharts(prev => ({ ...prev, [appointmentId]: [] })); }

      try {
        const docsResponse = await fetchDocsWithSignedUrlsApi(doctorId, appointmentId);
        const docList = Array.isArray(docsResponse?.data?.data)
          ? docsResponse.data.data
          : (Array.isArray(docsResponse?.data) ? docsResponse.data : []);
        setAppointmentDocuments(prev => ({ ...prev, [appointmentId]: docList }));
      } catch { setAppointmentDocuments(prev => ({ ...prev, [appointmentId]: [] })); }
    } finally {
      setIsLoadingAppointmentData(prev => ({ ...prev, [appointmentId]: false }));
    }
  }, [doctorId, patientId, isLoadingAppointmentData]);

  // Initial fetch
  useEffect(() => {
    if (patientId) {
      fetchPatientData();
      fetchFamilyMembers();
    }
  }, [patientId, fetchPatientData, fetchFamilyMembers]);

  // Initialize doctor info when userData loads
  useEffect(() => {
    if (userData?.users && !editedDoctorInfo) {
      const docInfo = {
        first_name: userData.users.first_name || "",
        last_name: userData.users.last_name || "",
        address: userData.users.address || "",
        cell_phone: userData.users.cell_phone || userData.users.phone || "",
        email: userData.users.email || "",
        clinic_name: userData.users.clinic_name || "",
        speciality: userData.users.speciality || "",
      };
      setEditedDoctorInfo(deepClone(docInfo));
      originalDoctorInfoRef.current = deepClone(docInfo);
    }
  }, [userData, editedDoctorInfo]);

  // Fetch additional data for selected appointments
  useEffect(() => {
    const selectedIds = Object.entries(selectedAppointments)
      .filter(([_, sel]) => sel)
      .map(([id]) => id);
    selectedIds.forEach(id => {
      if (!appointmentCareSteps[id] && !isLoadingAppointmentData[id]) {
        fetchAppointmentAdditionalData(id);
      }
    });
  }, [selectedAppointments, appointmentCareSteps, isLoadingAppointmentData, fetchAppointmentAdditionalData]);

  // --- Handlers ---

  const handleToggleAppointment = useCallback((appointmentId) => {
    setSelectedAppointments(prev => ({ ...prev, [appointmentId]: !prev[appointmentId] }));
  }, []);

  const handleSelectAll = useCallback(() => {
    const sel = {};
    editedAppointments.forEach(apt => { sel[apt.id] = true; });
    setSelectedAppointments(sel);
  }, [editedAppointments]);

  const handleDeselectAll = useCallback(() => {
    setSelectedAppointments({});
  }, []);

  const handleToggleFieldVisibility = useCallback((appointmentId, category, fieldKey) => {
    setFieldVisibility(prev => ({
      ...prev,
      [appointmentId]: {
        ...prev[appointmentId],
        [category]: {
          ...(prev[appointmentId]?.[category] || {}),
          [fieldKey]: !(prev[appointmentId]?.[category]?.[fieldKey] ?? true)
        }
      }
    }));
  }, []);

  const handleEditAppointmentField = useCallback((appointmentId, path, value) => {
    setEditedAppointments(prev => {
      const newAppts = [...prev];
      const idx = newAppts.findIndex(a => String(a.id) === String(appointmentId));
      if (idx === -1) return prev;

      const apt = deepClone(newAppts[idx]);
      const parts = path.split(".");
      let target = apt;
      for (let i = 0; i < parts.length - 1; i++) {
        const key = isNaN(parts[i]) ? parts[i] : parseInt(parts[i]);
        if (target[key] === undefined) return prev;
        target = target[key];
      }
      const lastKey = isNaN(parts[parts.length - 1]) ? parts[parts.length - 1] : parseInt(parts[parts.length - 1]);
      target[lastKey] = value;

      newAppts[idx] = apt;
      return newAppts;
    });
  }, []);

  const handleResetAppointment = useCallback((appointmentId) => {
    const original = originalAppointmentsRef.current.find(a => String(a.id) === String(appointmentId));
    if (!original) return;

    setEditedAppointments(prev =>
      prev.map(apt => String(apt.id) === String(appointmentId) ? deepClone(original) : apt)
    );
    setFieldVisibility(prev => ({
      ...prev,
      [appointmentId]: initializeFieldVisibility(appointmentId)
    }));
    toast.info("Consultation réinitialisée aux données d'origine");
  }, [initializeFieldVisibility]);

  const handleToggleCollapse = useCallback((appointmentId) => {
    setCollapsedAppointments(prev => ({ ...prev, [appointmentId]: !prev[appointmentId] }));
  }, []);

  const handleExpandAll = useCallback(() => {
    setCollapsedAppointments({});
  }, []);

  const handleCollapseAll = useCallback(() => {
    const collapsed = {};
    editedAppointments.forEach(apt => { collapsed[apt.id] = true; });
    setCollapsedAppointments(collapsed);
  }, [editedAppointments]);

  // Cover info handlers
  const handleEditCoverField = useCallback((section, field, value) => {
    if (section === "doctor") {
      setEditedDoctorInfo(prev => ({ ...prev, [field]: value }));
    } else {
      setEditedPatientInfo(prev => ({ ...prev, [field]: value }));
    }
  }, []);

  const handleToggleCoverVisibility = useCallback((section, field) => {
    setCoverFieldVisibility(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: !prev[section]?.[field],
      }
    }));
  }, []);

  const handleResetCoverInfo = useCallback(() => {
    if (originalPatientInfoRef.current) {
      setEditedPatientInfo(deepClone(originalPatientInfoRef.current));
    }
    if (originalDoctorInfoRef.current) {
      setEditedDoctorInfo(deepClone(originalDoctorInfoRef.current));
    }
    setCoverFieldVisibility({
      doctor: { name: true, address: true, phone: true, email: true },
      patient: { name: true, dob: true, gender: true, age: true },
      medical: { background: true, lifestyle: true, allergies: true, treatments: true },
    });
    toast.info("Informations de la page de garde réinitialisées");
  }, []);

  const handleFamilyMemberChange = useCallback((memberId) => {
    setSelectedFamilyMember(memberId);
    fetchPatientData(memberId);
  }, [fetchPatientData]);

  // Prepared appointments with additional data
  const preparedAppointments = useMemo(() => {
    return editedAppointments.map(apt => ({
      ...apt,
      careSteps: appointmentCareSteps[apt.id] || [],
      charts: appointmentCharts[apt.id] || [],
      documents: appointmentDocuments[apt.id] || [],
    }));
  }, [editedAppointments, appointmentCareSteps, appointmentCharts, appointmentDocuments]);

  // Convert fieldVisibility → selectedFields format for ReportPreview
  const getSelectedFieldsForAppointment = useCallback((appointmentId) => {
    const vis = fieldVisibility[appointmentId];
    if (!vis) return getDefaultSelectedFields();
    const result = {};
    Object.entries(vis).forEach(([cat, fields]) => {
      result[cat] = Object.entries(fields)
        .filter(([_, visible]) => visible)
        .map(([key]) => key);
    });
    return result;
  }, [fieldVisibility]);

  // Build appointmentFieldOverrides for ReportPreview
  const appointmentFieldOverrides = useMemo(() => {
    const overrides = {};
    editedAppointments.forEach(apt => {
      overrides[apt.id] = getSelectedFieldsForAppointment(apt.id);
    });
    return overrides;
  }, [editedAppointments, getSelectedFieldsForAppointment]);

  // Global selectedFields (first appointment's or defaults)
  const selectedFields = useMemo(() => {
    if (editedAppointments.length > 0) {
      return getSelectedFieldsForAppointment(editedAppointments[0].id);
    }
    return getDefaultSelectedFields();
  }, [editedAppointments, getSelectedFieldsForAppointment]);

  // Merge edited patient info for preview
  const patientInfoForPreview = useMemo(() => {
    const base = patientData?.data?.[0] || {};
    if (!editedPatientInfo) return base;
    return { ...base, ...editedPatientInfo };
  }, [patientData, editedPatientInfo]);

  // Merge edited doctor info for preview
  const doctorInfoForPreview = useMemo(() => {
    const base = userData?.users || {};
    if (!editedDoctorInfo) return base;
    return { ...base, ...editedDoctorInfo };
  }, [userData, editedDoctorInfo]);

  // Export handlers
  const handleExportPDF = useCallback(async () => {
    if (!reportPreviewRef.current) {
      toast.error("Aperçu du rapport non disponible");
      return;
    }
    setIsExporting(true);
    try {
      const pName = `${patientData?.data?.[0]?.first_name || ""}_${patientData?.data?.[0]?.last_name || ""}`;
      await generatePDFFromElement(reportPreviewRef.current, `rapport-medical_${pName}_${moment().format("YYYY-MM-DD")}.pdf`);
      toast.success("PDF exporté avec succès");
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast.error("Échec de l'export PDF");
    } finally {
      setIsExporting(false);
    }
  }, [patientData]);

  const handleExportJSON = useCallback(() => {
    setIsExporting(true);
    try {
      const selectedApts = preparedAppointments.filter(apt => selectedAppointments[apt.id]);
      const exportData = {
        patient: patientData?.data?.[0] || {},
        generatedAt: moment().toISOString(),
        appointments: selectedApts.map(apt => ({
          id: apt.id,
          date: apt.appointment_date,
          data: apt,
          selectedFields: getSelectedFieldsForAppointment(apt.id)
        })),
      };
      const pName = `${patientData?.data?.[0]?.first_name || ""}_${patientData?.data?.[0]?.last_name || ""}`;
      generateJSONExport(exportData, `rapport-medical_${pName}_${moment().format("YYYY-MM-DD")}.json`);
      toast.success("JSON exporté avec succès");
    } catch (error) {
      console.error("Error exporting JSON:", error);
      toast.error("Échec de l'export JSON");
    } finally {
      setIsExporting(false);
    }
  }, [preparedAppointments, selectedAppointments, patientData, getSelectedFieldsForAppointment]);

  const handlePrint = useCallback(() => { window.print(); }, []);

  const selectedCount = useMemo(() =>
    Object.values(selectedAppointments).filter(Boolean).length,
    [selectedAppointments]
  );

  const patientName = patientData?.data?.[0]
    ? `${patientData.data[0].first_name} ${patientData.data[0].last_name}`
    : "";

  if (isLoading) return <LoadingPage />;

  return (
    <div className="doctormain medical-report-page">
      <Sidebar />
      <div className="rightSec">
        <DashboardDoctorHeader />

        <div className="mainDiv wizard-container">
          {/* Page Header */}
          <div className="wizard-page-header">
            <div className="wizard-page-header__left">
              <button onClick={() => navigate(-1)} className="wizard-back-btn">
                <ArrowLeft size={20} color="#333" />
              </button>
              <div>
                <h1 className="wizard-page-title">
                  <FileText size={22} color="#687eff" />
                  Rapport Médical du Patient
                </h1>
                <p className="wizard-page-subtitle">
                  Modifier, sélectionner et exporter l'historique médical du patient
                </p>
              </div>
            </div>
            <button
              onClick={() => fetchPatientData(selectedFamilyMember)}
              disabled={isLoading}
              className="wizard-refresh-btn"
            >
              <RefreshCw size={14} /> Actualiser
            </button>
          </div>

          {/* Patient Header */}
          <PatientHeader
            patient={patientData?.data?.[0]}
            familyMembers={familyMembers}
            selectedMember={selectedFamilyMember}
            onSelectMember={handleFamilyMemberChange}
            showFamilySelector={true}
          />

          {/* Wizard Step Navigation */}
          <div className="wizard-steps">
            <button
              onClick={() => setWizardStep("edit")}
              className={`wizard-step-btn ${wizardStep === "edit" ? "wizard-step-btn--active" : ""}`}
            >
              <Edit3 size={16} />
              1. Modifier & Sélectionner
            </button>
            <button
              onClick={() => setWizardStep("preview")}
              className={`wizard-step-btn ${wizardStep === "preview" ? "wizard-step-btn--active" : ""}`}
            >
              <Eye size={16} />
              2. Aperçu & Export
            </button>
          </div>

          {/* === STEP 1: EDIT & SELECT === */}
          {wizardStep === "edit" && (
            <div>
              {/* Cover Page Info Editor */}
              <CoverInfoEditor
                variant="editor"
                doctorInfo={editedDoctorInfo}
                patientInfo={editedPatientInfo}
                coverFieldVisibility={coverFieldVisibility}
                onEditCoverField={handleEditCoverField}
                onToggleCoverVisibility={handleToggleCoverVisibility}
                onResetCoverInfo={handleResetCoverInfo}
              />

              {/* Bulk Actions Bar */}
              <div className="wizard-bulk-actions">
                <div className="wizard-bulk-actions__buttons">
                  <button onClick={handleSelectAll} className="wizard-action-btn">
                    <CheckSquare size={14} /> Tout sélectionner
                  </button>
                  <button onClick={handleDeselectAll} className="wizard-action-btn">
                    <Square size={14} /> Tout désélectionner
                  </button>
                  <button onClick={handleExpandAll} className="wizard-action-btn">
                    <ChevronsDown size={14} /> Tout déplier
                  </button>
                  <button onClick={handleCollapseAll} className="wizard-action-btn">
                    <ChevronsUp size={14} /> Tout replier
                  </button>
                </div>
                <span className="wizard-bulk-actions__count">
                  {selectedCount} / {editedAppointments.length} consultations sélectionnées
                </span>
              </div>

              {/* Consultation Editor */}
              <AppointmentSelector
                appointments={preparedAppointments}
                originalAppointments={originalAppointmentsRef.current}
                selectedAppointments={selectedAppointments}
                onToggleAppointment={handleToggleAppointment}
                fieldVisibility={fieldVisibility}
                onToggleFieldVisibility={handleToggleFieldVisibility}
                onEditField={handleEditAppointmentField}
                onResetAppointment={handleResetAppointment}
                collapsedAppointments={collapsedAppointments}
                onToggleCollapse={handleToggleCollapse}
              />

              {/* View Report Button */}
              <div className="wizard-view-report-wrapper">
                <button
                  onClick={() => setWizardStep("preview")}
                  disabled={selectedCount === 0}
                  className={`wizard-view-report-btn ${selectedCount > 0 ? "wizard-view-report-btn--enabled" : "wizard-view-report-btn--disabled"}`}
                >
                  <Eye size={18} /> Voir le rapport <ArrowRight size={16} />
                </button>
              </div>
            </div>
          )}

          {/* === STEP 2: PREVIEW & EXPORT === */}
          {wizardStep === "preview" && (
            <div>
              <div className="wizard-preview-back">
                <button onClick={() => setWizardStep("edit")} className="wizard-action-btn">
                  <ArrowLeft size={14} /> Retour à l'édition
                </button>
              </div>

              <div className="wizard-preview-container">
                <ReportPreview
                  ref={reportPreviewRef}
                  patientInfo={patientInfoForPreview}
                  appointments={preparedAppointments}
                  selectedAppointments={selectedAppointments}
                  selectedFields={selectedFields}
                  appointmentFieldOverrides={appointmentFieldOverrides}
                  doctorInfo={doctorInfoForPreview}
                  coverFieldVisibility={coverFieldVisibility}
                  onEditCoverField={handleEditCoverField}
                  showHeader={true}
                  showGeneratedDate={true}
                />
              </div>

              <div className="wizard-export-wrapper">
                <ExportActions
                  onExportPDF={handleExportPDF}
                  onExportJSON={handleExportJSON}
                  onPrint={handlePrint}
                  isExporting={isExporting}
                  exportDisabled={selectedCount === 0}
                  selectedCount={selectedCount}
                  patientName={patientName}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PatientMedicalReport;
